import type { Perfume, Collection, ScentFamily, Concentration, User } from '@/types'

import { API_BASE } from '@/lib/config'

type ApiProduct = {
  id: number
  slug: string
  name: string
  description: string
  family: string
  concentration: string
  story: string
  notes: { top: string[]; heart: string[]; base: string[] }
  images: string[]
  badges: string[]
  rating: number
  review_count: number
  is_new: boolean
  is_bestseller: boolean
  is_discovery_set: boolean
  labels: ('new' | 'bestseller' | 'discovery-set' | 'refill')[]
  price_base_kobo: number
  occasion_tags: string[]
  smells_like: string[]
  longevity_hours_min: number
  longevity_hours_max: number
  stock_qty: number
  sizes: {
    id: string
    size: string
    price: number
    price_kobo: number
    is_refillable: boolean
    in_stock: boolean
    stock_qty: number
  }[]
}

type ApiCollection = {
  id: number
  slug: string
  name: string
  tagline: string
  description: string
  image: string | null
  perfume_ids: number[]
}

const CACHE_TTL_MS = 60_000

let catalogueCache:
  | { at: number; promise: Promise<{ products: Perfume[]; collections: Collection[] }> }
  | null = null

function isKnownFamily(family: string): family is ScentFamily {
  return ['Citrus', 'Floral', 'Woody', 'Oriental', 'Fresh', 'Gourmand'].includes(family)
}

function isKnownConcentration(c: string): c is Concentration {
  return ['EDT', 'EDP', 'Parfum', 'Extrait'].includes(c)
}

function mapApiProduct(p: ApiProduct): Perfume {
  return {
    id: p.slug,
    apiId: p.id,
    name: p.name,
    family: isKnownFamily(p.family) ? p.family : 'Fresh',
    concentration: isKnownConcentration(p.concentration) ? p.concentration : 'EDP',
    description: p.description,
    story: p.story,
    notes: p.notes,
    images: p.images,
    badges: p.badges,
    rating: p.rating,
    reviewCount: p.review_count,
    isNew: p.is_new,
    isBestseller: p.is_bestseller,
    isDiscoverySet: p.is_discovery_set,
    labels: p.labels ?? [],
    occasionTags: p.occasion_tags,
    smellsLike: p.smells_like,
    longevityHoursMin: p.longevity_hours_min,
    longevityHoursMax: p.longevity_hours_max,
    stockQty: p.stock_qty,
    sizes: p.sizes.map((s) => ({
      id: s.id,
      size: s.size,
      price: s.price,
      isRefillable: s.is_refillable,
      inStock: s.in_stock,
      stockQty: s.stock_qty,
    })),
  }
}

function mapApiCollection(c: ApiCollection, productSlugById: Map<number, string>): Collection {
  return {
    id: c.slug,
    slug: c.slug,
    name: c.name,
    tagline: c.tagline,
    description: c.description,
    image: c.image ?? '',
    perfumeIds: c.perfume_ids
      .map((id) => productSlugById.get(id))
      .filter((slug): slug is string => !!slug),
  }
}

async function fetchJson<T>(path: string): Promise<T | null> {
  const url = `${API_BASE}${path}`
  try {
    const res = await fetch(url, { headers: { accept: 'application/json' } })
    if (!res.ok) return null
    return (await res.json()) as T
  } catch {
    return null
  }
}

async function fetchCatalogue(): Promise<{ products: Perfume[]; collections: Collection[] }> {
  const data = await fetchJson<{ success: boolean; products: ApiProduct[]; collections: ApiCollection[] }>(
    '/v1/catalogue'
  )
  if (!data?.success) return { products: [], collections: [] }

  const products = data.products.map(mapApiProduct)
  const productSlugById = new Map<number, string>(data.products.map((p) => [p.id, p.slug]))
  const collections = data.collections.map((c) => mapApiCollection(c, productSlugById))
  return { products, collections }
}



export async function getCatalogue(): Promise<{ products: Perfume[]; collections: Collection[] }> {
  const now = Date.now()
  if (catalogueCache && now - catalogueCache.at < CACHE_TTL_MS) {
    return catalogueCache.promise
  }

  const promise = fetchCatalogue().catch(() => ({ products: [], collections: [] }))
  catalogueCache = { at: now, promise }
  return promise
}

export type OrderAddress = {
  street: string
  city: string
  state: string
  lga?: string
  zip?: string
  country?: string
}

export type OrderItemInput = {
  productId: number
  variantId: number
  quantity: number
}

export type CreateOrderResponse = {
  success: true
  reference: string
  authorizationUrl: string
  amountKobo: number
}

export async function createOrder(payload: {
  email: string
  name?: string
  phone?: string
  address: OrderAddress
  items: OrderItemInput[]
  callbackUrl?: string
  discountCode?: string
  quote?: {
    quoteId: string
    provider: string
    serviceLevel: string
    feeKobo: number
    estimatedDays: number
  }
}): Promise<CreateOrderResponse | { success: false; error: string; details?: unknown }> {
  const res = await fetch(`${API_BASE}/v1/orders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  })
  const data = (await res.json()) as { success: boolean; reference?: string; authorizationUrl?: string; amountKobo?: number; error?: string; details?: unknown }
  if (!res.ok || !data.success) {
    return { success: false, error: data.error ?? 'order_failed', details: data.details }
  }
  return {
    success: true,
    reference: data.reference!,
    authorizationUrl: data.authorizationUrl!,
    amountKobo: data.amountKobo!,
  }
}

export async function verifyOrder(reference: string): Promise<{ status: string; success: boolean }> {
  const res = await fetch(`${API_BASE}/v1/orders/verify/${encodeURIComponent(reference)}`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
  })
  return (await res.json()) as { status: string; success: boolean }
}

export async function getProduct(slug: string): Promise<Perfume | undefined> {
  const data = await fetchJson<{ success: boolean; product: ApiProduct }>(`/v1/catalogue/${encodeURIComponent(slug)}`)
  if (data?.success && data.product) return mapApiProduct(data.product)
  return undefined
}

export function clearCatalogueCache(): void {
  catalogueCache = null
}

export type SearchResult = {
  id: number
  slug: string
  name: string
  family: string
  price: number
  price_kobo: number
  image: string
}

export async function searchCatalogue(query: string, limit = 12): Promise<SearchResult[]> {
  if (!query.trim()) return []
  const res = await fetch(
    `${API_BASE}/v1/catalogue/search?q=${encodeURIComponent(query)}&limit=${limit}`,
    { headers: { Accept: 'application/json' } }
  )
  const data = (await res.json()) as { success: boolean; results: SearchResult[] }
  return res.ok && data.success ? data.results : []
}

export async function getRelatedProducts(slug: string, limit = 8): Promise<SearchResult[]> {
  const res = await fetch(
    `${API_BASE}/v1/catalogue/${encodeURIComponent(slug)}/related?limit=${limit}`,
    { headers: { Accept: 'application/json' } }
  )
  const data = (await res.json()) as { success: boolean; results: SearchResult[] }
  return res.ok && data.success ? data.results : []
}

export type ProductReview = {
  id: string
  author: string
  rating: number
  title: string
  body: string
  date: string
  verified: boolean
}

export async function getProductReviews(slug: string): Promise<ProductReview[]> {
  const res = await fetch(
    `${API_BASE}/v1/catalogue/${encodeURIComponent(slug)}/reviews`,
    { headers: { Accept: 'application/json' } }
  )
  const data = (await res.json()) as { success: boolean; reviews: ProductReview[] }
  return res.ok && data.success ? data.reviews : []
}

export type CartApiItem = {
  perfumeId: string
  perfumeName: string
  sizeId: string
  sizeLabel: string
  price: number
  quantity: number
  image: string
}

function cartHeaders(sessionId?: string | null): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }
  if (sessionId) headers['x-session-id'] = sessionId
  return headers
}

/**
 * Reads the server cart. Returns `null` when the request failed — deliberately
 * distinct from `{ items: [] }`, which means the server genuinely holds no cart.
 * Callers must not treat a failure as "empty": the cart is written back as a full
 * replace, so pushing a local bag after a failed read would truncate the real one.
 */
export async function getCart(
  sessionId?: string | null
): Promise<{ items: CartApiItem[]; session_id?: string } | null> {
  const res = await fetch(`${API_BASE}/v1/cart`, {
    headers: cartHeaders(sessionId),
    credentials: 'include',
  })
  const data = (await res.json()) as { success: boolean; items: CartApiItem[]; session_id?: string }
  if (!res.ok || !data.success) return null
  return data
}

export async function syncCart(
  items: CartApiItem[],
  sessionId?: string | null
): Promise<CartApiItem[]> {
  const res = await fetch(`${API_BASE}/v1/cart`, {
    method: 'POST',
    headers: cartHeaders(sessionId),
    credentials: 'include',
    body: JSON.stringify({ items }),
  })
  const data = (await res.json()) as { success: boolean; items: CartApiItem[] }
  return data.success ? data.items : items
}

export async function addCartItem(
  item: CartApiItem,
  sessionId?: string | null
): Promise<CartApiItem[]> {
  const res = await fetch(`${API_BASE}/v1/cart/add`, {
    method: 'POST',
    headers: cartHeaders(sessionId),
    credentials: 'include',
    body: JSON.stringify({ item }),
  })
  const data = (await res.json()) as { success: boolean; items: CartApiItem[] }
  return data.success ? data.items : []
}

export async function removeCartItem(
  payload: { perfumeId: string; sizeId: string },
  sessionId?: string | null
): Promise<CartApiItem[]> {
  const res = await fetch(`${API_BASE}/v1/cart/remove`, {
    method: 'POST',
    headers: cartHeaders(sessionId),
    credentials: 'include',
    body: JSON.stringify(payload),
  })
  const data = (await res.json()) as { success: boolean; items: CartApiItem[] }
  return data.success ? data.items : []
}

export type DeliveryEstimate = {
  min_days: number
  max_days: number
  fee: number
  fee_kobo: number
  base_fee_kobo: number
  provider: string
}

export type ShippingQuote = {
  quoteId: string
  provider: string
  serviceLevel: string
  fee: number
  feeKobo: number
  currency: string
  estimatedDays: number
}

export async function estimateDelivery(address: {
  state: string
  lga?: string
  city?: string
}): Promise<DeliveryEstimate | null> {
  const params = new URLSearchParams({ state: address.state })
  if (address.lga) params.set('lga', address.lga)
  if (address.city) params.set('city', address.city)
  const res = await fetch(`${API_BASE}/v1/delivery/estimate?${params.toString()}`, {
    headers: { Accept: 'application/json' },
  })
  const data = (await res.json()) as { success: boolean; estimate?: DeliveryEstimate }
  return data.success && data.estimate ? data.estimate : null
}

export async function getLogisticsQuotes(payload: {
  address: OrderAddress
  items: OrderItemInput[]
}): Promise<{ quotes: ShippingQuote[] } | null> {
  const res = await fetch(`${API_BASE}/v1/delivery/quotes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  })
  const data = (await res.json()) as { success: boolean; quotes?: ShippingQuote[] }
  return data.success && data.quotes ? { quotes: data.quotes } : null
}

export type BuyNowResponse = {
  success: true
  reference: string
  authorizationUrl: string
  amountKobo: number
}

export async function buyNow(payload: {
  email: string
  name?: string
  phone?: string
  address: OrderAddress
  productId: number
  variantId: number
  quantity: number
  callbackUrl?: string
}): Promise<BuyNowResponse | { success: false; error: string; details?: unknown }> {
  const res = await fetch(`${API_BASE}/v1/orders/buy-now`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  })
  const data = (await res.json()) as {
    success: boolean
    reference?: string
    authorizationUrl?: string
    amountKobo?: number
    error?: string
    details?: unknown
  }
  if (!res.ok || !data.success) {
    return { success: false, error: data.error ?? 'buy_now_failed', details: data.details }
  }
  return {
    success: true,
    reference: data.reference!,
    authorizationUrl: data.authorizationUrl!,
    amountKobo: data.amountKobo!,
  }
}

export type OrderStatus = {
  success: boolean
  reference: string
  status: string
  total_kobo: number
  items: { name: string; size: string; quantity: number; price_kobo: number; image?: string }[]
  timeline: { status: string; at: number; note?: string }[]
  shipping?: {
    provider?: string
    tracking_number?: string
    label_url?: string
    estimated_min?: number
    estimated_max?: number
  }
  can_cancel: boolean
}

export async function getOrderStatus(reference: string): Promise<OrderStatus | null> {
  const res = await fetch(`${API_BASE}/v1/orders/${encodeURIComponent(reference)}`, {
    headers: { Accept: 'application/json' },
    credentials: 'include',
  })
  const data = (await res.json()) as OrderStatus
  return res.ok && data.success ? data : null
}

export async function cancelOrder(reference: string): Promise<{ success: boolean; error?: string }> {
  const res = await fetch(`${API_BASE}/v1/orders/${encodeURIComponent(reference)}/cancel`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    credentials: 'include',
  })
  return (await res.json()) as { success: boolean; error?: string }
}

// ---- customer profile ------------------------------------------------------

export type PreferencesInput = {
  preferences?: Record<string, unknown>
  notificationOptIn?: boolean
  onboardingCompletedAt?: number
}

export async function updatePreferences(input: PreferencesInput): Promise<User | null> {
  const res = await fetch(`${API_BASE}/v1/customers/me/preferences`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  })
  const data = (await res.json()) as { success: boolean; customer: User }
  return res.ok && data.success ? data.customer : null
}

export async function fetchWishlist(): Promise<string[]> {
  const res = await fetch(`${API_BASE}/v1/customers/me/wishlist`, {
    headers: { Accept: 'application/json' },
    credentials: 'include',
  })
  const data = (await res.json()) as { success: boolean; wishlistProductSlugs: string[] }
  return res.ok && data.success ? data.wishlistProductSlugs : []
}

export async function addToWishlist(productSlug: string): Promise<string[]> {
  const res = await fetch(`${API_BASE}/v1/customers/me/wishlist`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ productSlug }),
  })
  const data = (await res.json()) as { success: boolean; wishlistProductSlugs: string[] }
  return res.ok && data.success ? data.wishlistProductSlugs : []
}

export async function removeFromWishlist(productSlug: string): Promise<string[]> {
  const res = await fetch(`${API_BASE}/v1/customers/me/wishlist/${encodeURIComponent(productSlug)}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json' },
    credentials: 'include',
  })
  const data = (await res.json()) as { success: boolean; wishlistProductSlugs: string[] }
  return res.ok && data.success ? data.wishlistProductSlugs : []
}

// ---- ACCESS loyalty ---------------------------------------------------------

export type AccessTier = 'bronze' | 'silver' | 'gold'

export type AccessProgress = {
  nextTier: 'silver' | 'gold' | null
  percent: number
  spendPercent: number
  ordersPercent: number
  targetSpendKobo: number | null
  targetOrders: number | null
  remainingSpendKobo: number
  remainingOrders: number
}

export type AccessReward = {
  id: string
  title: string
  blurb: string
  tier: AccessTier
  unlocked: boolean
  unlockHint: string | null
}

export type AccessJourneyStep = {
  tier: AccessTier
  title: string
  current: boolean
  benefits: string[]
}

export type AccessLoyalty = {
  tier: AccessTier
  tierLabel: string
  lifetimeSpendKobo: number
  capturedOrderCount: number
  thresholds: Record<'silver' | 'gold', { minOrders: number; minSpendKobo: number }>
  progress: AccessProgress
  rewards: AccessReward[]
  journey: AccessJourneyStep[]
  showWelcome: boolean
}

export async function fetchAccessLoyalty(): Promise<AccessLoyalty | null> {
  const res = await fetch(`${API_BASE}/v1/customers/me/loyalty`, {
    headers: { Accept: 'application/json' },
    credentials: 'include',
  })
  const data = (await res.json()) as { success: boolean; loyalty: AccessLoyalty }
  return res.ok && data.success ? data.loyalty : null
}

// Records that the member has seen the "Welcome to ACCESS" interstitial for
/// [tier]. The server merges this into the existing preferences blob, so the
/// page can call it without a dedicated endpoint.
export async function ackAccessWelcome(tier: AccessTier): Promise<void> {
  await updatePreferences({ preferences: { accessSeenTier: tier } })
}

// ---- account management (order history, addresses, details) -----------------

export type OrderSummary = {
  reference: string
  status: string
  payment_status: string
  shipping_status: string | null
  total: number
  shipping_fee: number
  item_count: number
  created_at: number
  captured_at: number | null
  delivered_at: number | null
  cancelled_at: number | null
  can_cancel: boolean
}

export async function getMyOrders(): Promise<OrderSummary[]> {
  const res = await fetch(`${API_BASE}/v1/orders/me`, {
    headers: { Accept: 'application/json' },
    credentials: 'include',
  })
  const data = (await res.json()) as { success: boolean; orders: OrderSummary[] }
  return res.ok && data.success ? data.orders : []
}

export type CustomerAddress = {
  id: number
  label: string
  recipientName: string
  phone: string
  street: string
  city: string
  state: string
  lga?: string | null
  zip?: string | null
  country: string
  isDefault: boolean
}

export type CustomerAddressInput = {
  label: string
  recipientName: string
  phone: string
  street: string
  city: string
  state: string
  lga?: string
  zip?: string
  country?: string
  isDefault?: boolean
}

export async function listAddresses(): Promise<CustomerAddress[]> {
  const res = await fetch(`${API_BASE}/v1/customers/me/addresses`, {
    headers: { Accept: 'application/json' },
    credentials: 'include',
  })
  const data = (await res.json()) as { success: boolean; addresses: CustomerAddress[] }
  return res.ok && data.success ? data.addresses : []
}

export async function createAddress(input: CustomerAddressInput): Promise<CustomerAddress[] | null> {
  const res = await fetch(`${API_BASE}/v1/customers/me/addresses`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  })
  const data = (await res.json()) as { success: boolean; addresses: CustomerAddress[] }
  return res.ok && data.success ? data.addresses : null
}

export async function updateAddress(
  id: number,
  input: CustomerAddressInput
): Promise<CustomerAddress[] | null> {
  const res = await fetch(`${API_BASE}/v1/customers/me/addresses/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  })
  const data = (await res.json()) as { success: boolean; addresses: CustomerAddress[] }
  return res.ok && data.success ? data.addresses : null
}

export async function deleteAddress(id: number): Promise<CustomerAddress[] | null> {
  const res = await fetch(`${API_BASE}/v1/customers/me/addresses/${id}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json' },
    credentials: 'include',
  })
  const data = (await res.json()) as { success: boolean; addresses: CustomerAddress[] }
  return res.ok && data.success ? data.addresses : null
}

export async function updateProfile(input: { name?: string; phone?: string | null }): Promise<User | null> {
  const res = await fetch(`${API_BASE}/v1/customers/me/profile`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  })
  const data = (await res.json()) as { success: boolean; customer: User }
  return res.ok && data.success ? data.customer : null
}

export type ChangePasswordResult =
  | { success: true }
  | { success: false; error: string }

export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<ChangePasswordResult> {
  const res = await fetch(`${API_BASE}/v1/customers/me/password`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ currentPassword, newPassword }),
  })
  const data = (await res.json()) as { success: boolean; error?: string }
  return res.ok && data.success
    ? { success: true }
    : { success: false, error: data.error ?? 'password_change_failed' }
}

export type DeleteAccountResult = { success: true } | { success: false; error: string }

export async function deleteAccount(): Promise<DeleteAccountResult> {
  const res = await fetch(`${API_BASE}/v1/customers/me`, {
    method: 'DELETE',
    headers: { Accept: 'application/json' },
    credentials: 'include',
  })
  if (res.ok) return { success: true }
  const data = (await res.json().catch(() => ({}))) as { error?: string }
  return { success: false, error: data.error ?? 'delete_failed' }
}

// ---- homepage / content ------------------------------------------------------

export type HomeSection = {
  key: string
  order: number
  settings: Record<string, unknown>
}

export type HomeProductCard = {
  id: string
  slug: string
  name: string
  family: string
  concentration: string
  price: number
  price_kobo: number
  isNew: boolean
  isBestseller: boolean
  isDiscoverySet: boolean
  labels: ('new' | 'bestseller' | 'discovery-set' | 'refill')[]
  image: string
  sizes: {
    size: string
    price: number
    price_kobo: number
    isRefillable: boolean
    inStock: boolean
  }[]
}

export type HomeCollection = {
  id: string
  slug: string
  name: string
  tagline: string
  description: string
  image: string
  perfumeIds: number[]
}

export type HomeArticle = {
  slug: string
  title: string
  excerpt: string
  body: string
  image: string
  publishedAt: string | null
}

export type PromoModules = {
  banner?: {
    enabled?: boolean
    text?: string
    link?: string
    style?: 'pill' | 'strip'
    background?: string
  }
  streak?: {
    enabled?: boolean
    headline?: string
    body?: string
    ctaLabel?: string
  }
  midnightMarket?: {
    enabled?: boolean
    title?: string
    subtitle?: string
    startHour?: number
    endHour?: number
    productSlugs?: string[]
    active?: boolean
    productIds?: number[]
  }
}

export type HomeData = {
  success: boolean
  sections: HomeSection[]
  bestsellers: HomeProductCard[]
  newest: HomeProductCard[]
  collections: HomeCollection[]
  articles: HomeArticle[]
  promos?: PromoModules
}

export async function getHomeData(): Promise<HomeData | null> {
  return fetchJson<HomeData>('/v1/home')
}

export type CollectionDetail = HomeCollection & {
  products: HomeProductCard[]
}

export async function getCollections(): Promise<HomeCollection[] | null> {
  const data = await fetchJson<{ success: boolean; collections: HomeCollection[] }>('/v1/collections')
  return data?.success ? data.collections : null
}

export async function getCollection(slug: string): Promise<CollectionDetail | null> {
  const data = await fetchJson<{ success: boolean; collection: CollectionDetail }>(
    `/v1/collections/${encodeURIComponent(slug)}`
  )
  return data?.success ? data.collection : null
}

export async function getJournalArticles(): Promise<HomeArticle[] | null> {
  const data = await fetchJson<{ success: boolean; articles: HomeArticle[] }>('/v1/journal')
  return data?.success ? data.articles : null
}

export async function getJournalArticle(slug: string): Promise<HomeArticle | null> {
  const data = await fetchJson<{ success: boolean; article: HomeArticle }>(
    `/v1/journal/${encodeURIComponent(slug)}`
  )
  return data?.success ? data.article : null
}

// ---- engagement: sky streak, mystery spin, drops ----------------------------

export type StreakReward = {
  code: string
  label: string
  kind: 'percent' | 'fixed'
  value: number
  expiresAt: number | null
}

export type CustomerStreak = {
  current: number
  longest: number
  lastVisitDate: string | null
  activeToday: boolean
  nextRewardAt: number | null
  mystery: StreakReward | null
}

export async function fetchStreak(): Promise<CustomerStreak | null> {
  const res = await fetch(`${API_BASE}/v1/customers/me/streak`, {
    headers: { Accept: 'application/json' },
    credentials: 'include',
  })
  const data = (await res.json()) as { success: boolean; streak: CustomerStreak }
  return res.ok && data.success ? data.streak : null
}

export async function recordStreakVisit(): Promise<CustomerStreak | null> {
  const res = await fetch(`${API_BASE}/v1/customers/me/streak/visit`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    credentials: 'include',
  })
  const data = (await res.json()) as { success: boolean; streak: CustomerStreak }
  return res.ok && data.success ? data.streak : null
}

export type SpinDiscount = {
  code: string
  kind: 'percent' | 'fixed'
  value: number
  label: string
  expiresAt: number
  alreadySpun: boolean
}

export type DiscountError = { success: false; error: string }

// One spin per customer; a repeat call returns the same prize, never a second.
export async function spinDiscount(): Promise<
  { success: true; discount: SpinDiscount } | DiscountError
> {
  const res = await fetch(`${API_BASE}/v1/discounts/spin`, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    credentials: 'include',
  })
  const data = (await res.json()) as { success: boolean; discount?: SpinDiscount; error?: string }
  if (!res.ok || !data.success) {
    return { success: false, error: data.error ?? 'spin_failed' }
  }
  return { success: true, discount: data.discount! }
}

export type DiscountQuote = {
  code: string
  kind: 'percent' | 'fixed'
  value: number
  label: string
  amountKobo: number
}

// Quotes a code against the basket. The same resolver runs when the order is
// placed, so what this returns is what gets charged.
export async function previewDiscount(
  code: string,
  itemsKobo: number
): Promise<{ success: true; discount: DiscountQuote } | DiscountError> {
  const res = await fetch(`${API_BASE}/v1/discounts/preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ code, itemsKobo }),
  })
  const data = (await res.json()) as { success: boolean; discount?: DiscountQuote; error?: string }
  if (!res.ok || !data.success) {
    return { success: false, error: data.error ?? 'discount_invalid' }
  }
  return { success: true, discount: data.discount! }
}
export type CustomerDiscount = {
  id: number
  code: string
  kind: 'percent' | 'fixed'
  value: number
  label: string
  minSubtotalKobo: number
  maxDiscountKobo: number | null
  source: string
  expiresAt: number | null
  redeemed: boolean
  available: boolean
}

export async function fetchCustomerDiscounts(): Promise<CustomerDiscount[]> {
  const res = await fetch(`${API_BASE}/v1/customers/me/discounts`, {
    headers: { Accept: 'application/json' },
    credentials: 'include',
  })
  const data = (await res.json()) as { success: boolean; discounts: CustomerDiscount[] }
  return res.ok && data.success ? data.discounts : []
}

export type NextDrop = {
  name: string
  blurb: string
  startsAt: number
  endsAt: number
  isLive: boolean
  productSlugs: string[]
}

export async function fetchNextDrop(): Promise<NextDrop | null> {
  const res = await fetch(`${API_BASE}/v1/drops/next`, {
    headers: { Accept: 'application/json' },
  })
  const data = (await res.json()) as { success: boolean; drop: NextDrop | null }
  return res.ok && data.success ? data.drop : null
}

// Human copy for the discount errors the API returns, so checkout never shows
// a bare `discount_min_not_met`.
export const DISCOUNT_MESSAGES: Record<string, string> = {
  discount_invalid: 'Enter a code to apply.',
  discount_not_found: 'That code is not recognised.',
  discount_not_started: 'That code is not active yet.',
  discount_expired: 'That code has expired.',
  discount_not_available: 'That code belongs to another account.',
  discount_min_not_met: 'That code needs a higher subtotal.',
  discount_exhausted: 'That code has been fully claimed.',
  discount_already_used: 'You have already used that code.',
  spin_failed: 'We could not spin just now. Please try again.',
}